import { Fragment, type ReactNode, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  isPriceReferenceContextInvalidError,
  isSessionDuplicateInvariantError,
  sessionQueryOptions,
} from "~/app/api/pageData";
import { hydrateUserPreferences } from "~/domains/userPreferences/redux/userPreferencesSlice";
import { clearSessionRuntimeState, setSessionRuntimeState } from "~/domains/user/sessionRuntime";
import type { SessionCartIdentityData, SessionData } from "~/app/store/bffApi";
import { setRuntimeConfig } from "~/common/config/runtimeConfig";
import { seedNotificationsFromSessionAction } from "~/features/notifications/redux/actions";
import { populateInitialComparisonList } from "~/features/compareProducts/redux/actions";
import { replaceSalesPartnerContext } from "~/domains/affiliate/redux/actions";
import { SessionRecoveryAlert } from "./SessionRecoveryAlert";
import { ACCOUNT_SELECTION_URL } from "~/utils/url/staticUrls";
import {
  isAccountContextTransitionInProgress,
  subscribeToAccountContextTransition,
} from "~/app/accountContextTransitionState";

export type SessionAvailability = "loading" | "ready" | "duplicateInvariant" | "unavailable";

export interface SessionState {
  availability: SessionAvailability;
  cartActionsDisabled: boolean;
  data: SessionData | null;
  error: unknown;
  duplicateRecoveryActiveCart: SessionCartIdentityData | null;
  priceReferenceContextInvalid: boolean;
  transportError: boolean;
  retry: () => void;
  synchronized: boolean;
}

/**
 * Exposes Session Data and its cart-invariant state without falling back to a stale successful
 * projection after a strict duplicate-cart 409.
 */
export function useSessionState(): SessionState {
  const query = useQuery(sessionQueryOptions());
  const accountTransitionInProgress = useSyncExternalStore(
    subscribeToAccountContextTransition,
    isAccountContextTransitionInProgress,
    isAccountContextTransitionInProgress,
  );
  const duplicateInvariant = isSessionDuplicateInvariantError(query.error);
  const priceReferenceContextInvalid = isPriceReferenceContextInvalidError(query.error);
  const data = duplicateInvariant || accountTransitionInProgress ? null : query.data ?? null;
  const transportError =
    !accountTransitionInProgress &&
    Boolean(query.error) &&
    !duplicateInvariant &&
    !priceReferenceContextInvalid &&
    !data;
  const availability: SessionAvailability = accountTransitionInProgress
    ? "loading"
    : duplicateInvariant
    ? "duplicateInvariant"
    : data
    ? "ready"
    : query.isPending
    ? "loading"
    : "unavailable";

  return {
    availability,
    cartActionsDisabled: duplicateInvariant,
    data,
    error: query.error,
    duplicateRecoveryActiveCart: duplicateInvariant ? query.data?.activeCart ?? null : null,
    priceReferenceContextInvalid,
    transportError,
    retry: () => void query.refetch(),
    synchronized: availability === "ready" && !query.isFetching,
  };
}

export function useSession() {
  return useSessionState().data;
}

/**
 * Triggers the Session Data fetch (GET /page-data/session) for the current visitor, including
 * anonymous visitors (whose user/account fields are null), and mirrors session-scoped date/account
 * flags into the runtime-config registry (ADR 0003 interim layer-threading).
 *
 * Session Data is never cacheable across visitors. Identity changes (login, logout, account switch,
 * currency/language switch) and explicit cart lifecycle transitions invalidate the
 * {@code ["page-data", "session"]} query key. Ordinary cart entry mutations do not: only Active Cart and
 * Merge Source Cart identity changes belong in this model projection (ADR 0003).
 *
 * On resolve/refetch the registry is updated so `src/common` utils observe fresh date flags.
 * A failed Session transport or invalid price-reference context blocks the application subtree;
 * the account-selection page remains available for recovering an invalid context.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const { availability, data, error, priceReferenceContextInvalid, retry, synchronized, transportError } =
    useSessionState();
  const [runtimeVersion, setRuntimeVersion] = useState(0);
  const runtimeSnapshotKeyRef = useRef("cleared");
  const nextRuntimeSnapshotKey = data ? runtimeSnapshotKey(data) : "cleared";

  useEffect(() => {
    if (!data) {
      clearSessionRuntimeState();
      dispatch(replaceSalesPartnerContext());
      if (runtimeSnapshotKeyRef.current !== "cleared") {
        runtimeSnapshotKeyRef.current = "cleared";
        setRuntimeVersion((version) => version + 1);
      }
      return;
    }
    // Cached Session Data remains the active runtime snapshot during a background refetch.
    // Publish and remount only after a synchronized response changes synchronous runtime inputs.
    if (!synchronized) {
      return;
    }
    setSessionRuntimeState(
      data.user,
      data.commerceContext.selectedAccount ?? null,
      Boolean(data.commerceContext.showExclusivePack),
    );
    dispatch(hydrateUserPreferences(data.user.preferences ?? {}));
    dispatch(replaceSalesPartnerContext(data.salesPartnerContext));
    if (runtimeSnapshotKeyRef.current !== nextRuntimeSnapshotKey) {
      runtimeSnapshotKeyRef.current = nextRuntimeSnapshotKey;
      setRuntimeVersion((version) => version + 1);
    }
  }, [data, dispatch, nextRuntimeSnapshotKey, synchronized]);

  useEffect(() => {
    if (data) {
      const { commerceContext } = data;
      setRuntimeConfig({
        punchoutMode: commerceContext.punchoutMode ?? null,
        showSundayAsDeliveryDay: Boolean(commerceContext.sundayAsDeliveryDayEnabled),
        showDateAsCalendarWeek: Boolean(commerceContext.shipDateToWeekEnabled),
        phaseOutEnabled: Boolean(commerceContext.phaseOutEnabled),
        lineLevelCustomerOrderNumberEnabled: Boolean(commerceContext.lineLevelCustomerOrderNumberEnabled),
        packageRestrictionEnabled: Boolean(commerceContext.packageRestrictionEnabled),
        multipleAccountSearchEnabled: Boolean(commerceContext.multipleAccountSearchEnabled),
        salesCampaignDaysLimitRDD: commerceContext.salesCampaignDaysLimitRDD,
        deliveryDateOffset: commerceContext.offsetRDD ?? 0,
      });
    } else {
      setRuntimeConfig({
        punchoutMode: null,
        lineLevelCustomerOrderNumberEnabled: false,
        packageRestrictionEnabled: false,
        multipleAccountSearchEnabled: false,
      });
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      dispatch(seedNotificationsFromSessionAction(data.user.newNotifications));
    }
  }, [data?.user.newNotifications, dispatch]);

  useEffect(() => {
    if (data) {
      dispatch(populateInitialComparisonList(data.productComparison.productCodes ?? []));
    }
  }, [data?.productComparison.productCodes, dispatch]);

  const accountSelectionPage = window.location.pathname.endsWith(ACCOUNT_SELECTION_URL);
  const recoveryBlocksChildren = transportError || (priceReferenceContextInvalid && !accountSelectionPage);
  const sessionRuntimeReady =
    availability !== "loading" && (!data || runtimeSnapshotKeyRef.current === nextRuntimeSnapshotKey);

  // Some legacy-compatible helpers are synchronous (for example permission checks used by URL builders).
  // Remount the application subtree whenever the Session-backed runtime snapshot changes so those helpers
  // cannot retain an anonymous/previous-account classification after Session resolves or is cleared.
  if (recoveryBlocksChildren) {
    return (
      <Fragment key={runtimeVersion}>
        <SessionRecoveryAlert
          error={error}
          onRetry={retry}
          priceReferenceContextInvalid={priceReferenceContextInvalid}
          transportError={transportError}
        />
      </Fragment>
    );
  }

  if (!sessionRuntimeReady) {
    return null;
  }

  return <Fragment key={runtimeVersion}>{children}</Fragment>;
}

function runtimeSnapshotKey(data: SessionData): string {
  const { selectedAccount, showExclusivePack } = data.commerceContext;
  return JSON.stringify({
    user: {
      uid: data.user.uid,
      lmsState: data.user.lmsState,
      roles: [...data.user.roles].sort(),
    },
    selectedAccount: selectedAccount
      ? {
          displayUid: selectedAccount.displayUid,
          name: selectedAccount.name,
          uid: selectedAccount.uid,
          endCustomerCode: selectedAccount.endCustomerCode,
          endCustomerName: selectedAccount.endCustomerName,
          salesOrg: selectedAccount.salesOrg,
        }
      : null,
    showExclusivePack: Boolean(showExclusivePack),
  });
}
